import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

const Register: React.FC = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    restaurantName: "",
    restaurantAddress: "",
    phone: "",
    email: "",
    businessId: "",
  });
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (field: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [field]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/signup-requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || t("register.failed"));
        setLoading(false);
        return;
      }
      setSubmitted(true);
    } catch {
      setError(t("register.networkError"));
    }
    setLoading(false);
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-stone-50 px-4">
        <img
          src="/reviewcounter-logo.png"
          alt="ReviewCounter"
          className="w-52 max-w-xs mb-6 object-contain"
        />
        <div className="bg-white p-6 rounded-xl shadow max-w-sm w-full text-center space-y-3">
          <h1 className="text-2xl font-bold text-teal-600">{t("register.receivedTitle")}</h1>
          <p className="text-gray-600 text-sm">{t("register.receivedBody")}</p>
          <Link to="/login" className="inline-block text-teal-600 hover:text-teal-800 text-sm underline">
            {t("register.backToSignIn")}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-stone-50 px-4 py-10">
      <img
        src="/reviewcounter-logo.png"
        alt="ReviewCounter"
        className="w-52 max-w-xs mb-6 object-contain"
      />
      <form onSubmit={handleSubmit} className="bg-white p-6 rounded-xl shadow max-w-sm w-full space-y-4">
        <h1 className="text-2xl font-bold text-center text-teal-600">{t("register.title")}</h1>
        <p className="text-gray-600 text-sm text-center">{t("register.subtitle")}</p>
        <div className="grid grid-cols-2 gap-3">
          <input
            className="border rounded p-2"
            type="text"
            placeholder={t("register.firstName")}
            value={form.firstName}
            onChange={handleChange("firstName")}
            required
          />
          <input
            className="border rounded p-2"
            type="text"
            placeholder={t("register.lastName")}
            value={form.lastName}
            onChange={handleChange("lastName")}
            required
          />
        </div>
        <input
          className="w-full border rounded p-2"
          type="text"
          placeholder={t("register.restaurantName")}
          value={form.restaurantName}
          onChange={handleChange("restaurantName")}
          required
        />
        <input
          className="w-full border rounded p-2"
          type="text"
          placeholder={t("register.restaurantAddress")}
          value={form.restaurantAddress}
          onChange={handleChange("restaurantAddress")}
          required
        />
        <input
          className="w-full border rounded p-2"
          type="tel"
          placeholder={t("register.phone")}
          value={form.phone}
          onChange={handleChange("phone")}
          required
        />
        <input
          className="w-full border rounded p-2"
          type="email"
          placeholder={t("register.email")}
          value={form.email}
          onChange={handleChange("email")}
          required
        />
        <input
          className="w-full border rounded p-2"
          type="text"
          placeholder={t("register.businessId")}
          value={form.businessId}
          onChange={handleChange("businessId")}
          required
        />
        <p className="text-gray-500 text-xs">{t("register.businessIdHint")}</p>
        {error && <div className="text-red-600 text-sm text-center">{error}</div>}
        <button
          type="submit"
          className="w-full bg-teal-600 text-white py-2 rounded font-semibold hover:bg-teal-700 transition"
          disabled={loading}
        >
          {loading ? t("register.submitting") : t("register.submit")}
        </button>
        <div className="text-center">
          <Link to="/login" className="text-teal-600 hover:text-teal-800 text-sm underline">
            {t("register.backToSignIn")}
          </Link>
        </div>
      </form>
    </div>
  );
};

export default Register;
